import React, { useState, useEffect } from 'react';
import { useAuth } from '../context/AuthContext';

const TierModal = ({ tier, onSave, onClose }) => {
  const { user } = useAuth();
  const [activeTab, setActiveTab] = useState('basic');
  const [formData, setFormData] = useState({
    name: '',
    internal_name: '',
    slug: '',
    tier_code: '',
    description: '',
    price_monthly: '',
    price_yearly: '',
    currency: 'Ksh',
    max_users: 1,
    max_warehouses: 1,
    max_storage_locations: '',
    support_level: 'standard',
    support_hours: '',
    support_channels: [],
    is_popular: false,
    is_active: true,
    display_order: 0,
    features: [],
    technical_notes: '',
    sales_notes: '',
    cost_per_license: '',
    min_commitment_months: 1,
    setup_fee: 0,
    renewal_discount_percent: 0,
    api_rate_limit: '',
    data_retention_days: 365,
    backup_frequency: 'daily',
    sla_uptime_percent: '',
  });
  const [newFeature, setNewFeature] = useState('');
  const supportChannelOptions = ['email', 'phone', 'chat', 'ticket', 'dedicated_manager'];

  useEffect(() => {
    if (tier) {
      const features = typeof tier.features === 'string' 
        ? JSON.parse(tier.features) 
        : tier.features || [];
      const supportChannels = typeof tier.support_channels === 'string'
        ? JSON.parse(tier.support_channels)
        : tier.support_channels || [];
      
      setFormData({
        name: tier.name || '',
        internal_name: tier.internal_name || '',
        slug: tier.slug || '',
        tier_code: tier.tier_code || '',
        description: tier.description || '',
        price_monthly: tier.price_monthly || '',
        price_yearly: tier.price_yearly || '',
        currency: tier.currency || 'Ksh',
        max_users: tier.max_users || 1,
        max_warehouses: tier.max_warehouses || 1,
        max_storage_locations: tier.max_storage_locations || '',
        support_level: tier.support_level || 'standard',
        support_hours: tier.support_hours || '',
        support_channels: supportChannels,
        is_popular: tier.is_popular || false,
        is_active: tier.is_active !== undefined ? tier.is_active : true,
        display_order: tier.display_order || 0,
        features: features,
        technical_notes: tier.technical_notes || '',
        sales_notes: tier.sales_notes || '',
        cost_per_license: tier.cost_per_license || '',
        min_commitment_months: tier.min_commitment_months || 1,
        setup_fee: tier.setup_fee || 0,
        renewal_discount_percent: tier.renewal_discount_percent || 0,
        api_rate_limit: tier.api_rate_limit || '',
        data_retention_days: tier.data_retention_days || 365,
        backup_frequency: tier.backup_frequency || 'daily',
        sla_uptime_percent: tier.sla_uptime_percent || '',
      });
    }
  }, [tier]);

  const handleChange = (e) => {
    const { name, value, type, checked } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: type === 'checkbox' ? checked : value,
    }));
  };

  const handleSupportChannelToggle = (channel) => {
    setFormData(prev => {
      const channels = prev.support_channels || [];
      const newChannels = channels.includes(channel)
        ? channels.filter(c => c !== channel)
        : [...channels, channel];
      return { ...prev, support_channels: newChannels };
    });
  };

  const handleAddFeature = () => {
    if (newFeature.trim()) {
      setFormData(prev => ({
        ...prev,
        features: [...prev.features, newFeature.trim()]
      }));
      setNewFeature('');
    }
  };

  const handleRemoveFeature = (index) => {
    setFormData(prev => ({
      ...prev,
      features: prev.features.filter((_, i) => i !== index)
    }));
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    onSave({
      ...formData,
      created_by: tier ? undefined : user?.id,
      last_modified_by: tier ? user?.id : undefined
    });
  };

  const generateSlug = (name) => {
    return name
      .toLowerCase()
      .replace(/[^a-z0-9]+/g, '-')
      .replace(/(^-|-$)/g, '');
  };

  const handleNameChange = (e) => {
    const name = e.target.value;
    setFormData(prev => ({
      ...prev,
      name,
      slug: prev.slug || generateSlug(name),
    }));
  };

  return (
    <div className="modal fade show" style={{ display: 'block' }} tabIndex="-1">
      <div className="modal-dialog modal-xl" style={{ zIndex: 1051 }}>
        <div className="modal-content" style={{ backgroundColor: '#fff', opacity: 1, filter: 'none' }}>
          <div className="modal-header">
            <h4 className="modal-title">{tier ? 'Edit License Tier' : 'Create New License Tier'}</h4>
            <button type="button" className="close" onClick={onClose}>
              <span>&times;</span>
            </button>
          </div>
          <form onSubmit={handleSubmit}>
            <div className="modal-body">
              {/* Tabs */}
              <ul className="nav nav-tabs mb-3">
                <li className="nav-item">
                  <a className={`nav-link ${activeTab === 'basic' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); setActiveTab('basic'); }}>
                    Basic Info
                  </a>
                </li>
                <li className="nav-item">
                  <a className={`nav-link ${activeTab === 'pricing' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); setActiveTab('pricing'); }}>
                    Pricing & Limits
                  </a>
                </li>
                <li className="nav-item">
                  <a className={`nav-link ${activeTab === 'support' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); setActiveTab('support'); }}>
                    Support & SLA
                  </a>
                </li>
                <li className="nav-item">
                  <a className={`nav-link ${activeTab === 'technical' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); setActiveTab('technical'); }}>
                    Technical
                  </a>
                </li>
                <li className="nav-item">
                  <a className={`nav-link ${activeTab === 'internal' ? 'active' : ''}`} href="#" onClick={(e) => { e.preventDefault(); setActiveTab('internal'); }}>
                    Internal Notes
                  </a>
                </li>
              </ul>

              {/* Basic Info Tab */}
              {activeTab === 'basic' && (
                <div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Tier Name (Public) *</label>
                        <input
                          type="text"
                          className="form-control"
                          name="name"
                          value={formData.name}
                          onChange={handleNameChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Internal Name</label>
                        <input
                          type="text"
                          className="form-control"
                          name="internal_name"
                          value={formData.internal_name}
                          onChange={handleChange}
                          placeholder="Internal reference name"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Slug *</label>
                        <input
                          type="text"
                          className="form-control"
                          name="slug"
                          value={formData.slug}
                          onChange={handleChange}
                          required
                          pattern="[a-z0-9-]+"
                        />
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Tier Code</label>
                        <input
                          type="text"
                          className="form-control"
                          name="tier_code"
                          value={formData.tier_code}
                          onChange={handleChange}
                          placeholder="e.g., TIER-STARTER"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Description (Public)</label>
                    <textarea
                      className="form-control"
                      name="description"
                      rows="2"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="Customer-facing description"
                    />
                  </div>
                  <div className="form-group">
                    <label>Features (Public)</label>
                    <div className="input-group mb-2">
                      <input
                        type="text"
                        className="form-control"
                        placeholder="Add feature"
                        value={newFeature}
                        onChange={(e) => setNewFeature(e.target.value)}
                        onKeyPress={(e) => {
                          if (e.key === 'Enter') {
                            e.preventDefault();
                            handleAddFeature();
                          }
                        }}
                      />
                      <div className="input-group-append">
                        <button type="button" className="btn btn-default" onClick={handleAddFeature}>
                          <i className="fas fa-plus"></i>
                        </button>
                      </div>
                    </div>
                    <ul className="list-group" style={{ maxHeight: '150px', overflowY: 'auto' }}>
                      {formData.features.map((feature, index) => (
                        <li key={index} className="list-group-item d-flex justify-content-between align-items-center" style={{ padding: '4px 8px' }}>
                          <span style={{ fontSize: '11px' }}>{feature}</span>
                          <button
                            type="button"
                            className="btn btn-sm btn-danger"
                            onClick={() => handleRemoveFeature(index)}
                            style={{ padding: '2px 6px', fontSize: '10px' }}
                          >
                            <i className="fas fa-times"></i>
                          </button>
                        </li>
                      ))}
                    </ul>
                  </div>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="is_popular"
                          checked={formData.is_popular}
                          onChange={handleChange}
                        />
                        <label className="form-check-label">Mark as Popular</label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-check">
                        <input
                          className="form-check-input"
                          type="checkbox"
                          name="is_active"
                          checked={formData.is_active}
                          onChange={handleChange}
                        />
                        <label className="form-check-label">Active</label>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Display Order</label>
                        <input
                          type="number"
                          className="form-control"
                          name="display_order"
                          value={formData.display_order}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Pricing & Limits Tab */}
              {activeTab === 'pricing' && (
                <div>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Monthly Price *</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          name="price_monthly"
                          value={formData.price_monthly}
                          onChange={handleChange}
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Yearly Price</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          name="price_yearly"
                          value={formData.price_yearly}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Currency</label>
                        <input
                          type="text"
                          className="form-control"
                          name="currency"
                          value={formData.currency}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Cost per License (Internal)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          name="cost_per_license"
                          value={formData.cost_per_license}
                          onChange={handleChange}
                          placeholder="Internal cost"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Setup Fee</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          name="setup_fee"
                          value={formData.setup_fee}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Renewal Discount (%)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          name="renewal_discount_percent"
                          value={formData.renewal_discount_percent}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Max Users *</label>
                        <input
                          type="number"
                          className="form-control"
                          name="max_users"
                          value={formData.max_users}
                          onChange={handleChange}
                          min="1"
                          required
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Max Warehouses</label>
                        <input
                          type="number"
                          className="form-control"
                          name="max_warehouses"
                          value={formData.max_warehouses}
                          onChange={handleChange}
                          min="1"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Max Storage Locations</label>
                        <input
                          type="number"
                          className="form-control"
                          name="max_storage_locations"
                          value={formData.max_storage_locations}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Min Commitment (Months)</label>
                        <input
                          type="number"
                          className="form-control"
                          name="min_commitment_months"
                          value={formData.min_commitment_months}
                          onChange={handleChange}
                          min="1"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>API Rate Limit (per minute)</label>
                        <input
                          type="number"
                          className="form-control"
                          name="api_rate_limit"
                          value={formData.api_rate_limit}
                          onChange={handleChange}
                          placeholder="e.g., 1000"
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Support & SLA Tab */}
              {activeTab === 'support' && (
                <div>
                  <div className="row">
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Support Level</label>
                        <select
                          className="form-control"
                          name="support_level"
                          value={formData.support_level}
                          onChange={handleChange}
                        >
                          <option value="basic">Basic</option>
                          <option value="standard">Standard</option>
                          <option value="priority">Priority</option>
                          <option value="enterprise">Enterprise</option>
                          <option value="dedicated">Dedicated</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-6">
                      <div className="form-group">
                        <label>Support Hours</label>
                        <input
                          type="text"
                          className="form-control"
                          name="support_hours"
                          value={formData.support_hours}
                          onChange={handleChange}
                          placeholder="e.g., 9am-5pm EAT or 24/7"
                        />
                      </div>
                    </div>
                  </div>
                  <div className="form-group">
                    <label>Support Channels</label>
                    <div className="row">
                      {supportChannelOptions.map(channel => (
                        <div key={channel} className="col-md-3">
                          <div className="form-check">
                            <input
                              className="form-check-input"
                              type="checkbox"
                              checked={formData.support_channels.includes(channel)}
                              onChange={() => handleSupportChannelToggle(channel)}
                            />
                            <label className="form-check-label text-capitalize">
                              {channel}
                            </label>
                          </div>
                        </div>
                      ))}
                    </div>
                  </div>
                  <div className="row">
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>SLA Uptime (%)</label>
                        <input
                          type="number"
                          step="0.01"
                          className="form-control"
                          name="sla_uptime_percent"
                          value={formData.sla_uptime_percent}
                          onChange={handleChange}
                          placeholder="e.g., 99.9"
                        />
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Backup Frequency</label>
                        <select
                          className="form-control"
                          name="backup_frequency"
                          value={formData.backup_frequency}
                          onChange={handleChange}
                        >
                          <option value="daily">Daily</option>
                          <option value="weekly">Weekly</option>
                          <option value="real-time">Real-time</option>
                        </select>
                      </div>
                    </div>
                    <div className="col-md-4">
                      <div className="form-group">
                        <label>Data Retention (Days)</label>
                        <input
                          type="number"
                          className="form-control"
                          name="data_retention_days"
                          value={formData.data_retention_days}
                          onChange={handleChange}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              )}

              {/* Technical Tab */}
              {activeTab === 'technical' && (
                <div>
                  <div className="form-group">
                    <label>Technical Notes</label>
                    <textarea
                      className="form-control"
                      name="technical_notes"
                      rows="8"
                      value={formData.technical_notes}
                      onChange={handleChange}
                      placeholder="Internal technical notes, requirements, limitations, etc."
                    />
                  </div>
                </div>
              )}

              {/* Internal Notes Tab */}
              {activeTab === 'internal' && (
                <div>
                  <div className="form-group">
                    <label>Sales Notes</label>
                    <textarea
                      className="form-control"
                      name="sales_notes"
                      rows="8"
                      value={formData.sales_notes}
                      onChange={handleChange}
                      placeholder="Internal sales notes, target market, positioning, etc."
                    />
                  </div>
                </div>
              )}
            </div>
            <div className="modal-footer">
              <button type="button" className="btn btn-default btn-sm" onClick={onClose}>
                Cancel
              </button>
              <button type="submit" className="btn btn-primary btn-sm">
                {tier ? 'Update' : 'Create'} Tier
              </button>
            </div>
          </form>
        </div>
      </div>
      <div className="modal-backdrop fade show" style={{ backgroundColor: 'rgba(0, 0, 0, 0.5)', zIndex: 1050 }} onClick={onClose}></div>
    </div>
  );
};

export default TierModal;
